/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace variable-name align */

import { first }                          from 'rxjs/operators';

import { ProjectElement }                 from './project.element';
import {
  Blob as FileBlob,
  BlobType }                              from '../blob';
import { Project }                        from '../project';
import { ProjectElementFactoryDecorator } from '@/models/project/project.element.factory.decorator';
import { JSONBrowserMetadata }            from '@/screens/ide/json.browser.metadata';
import { basename, errorAsText }          from '@/util/util';
import { TextEditorMetadata }             from '@/screens/ide/text.editor.metadata';
import { Logger }                         from '@/util/logger';
import { BlobElement }                    from '@/models/project/blob.element';
import { ProjectElementType }             from '@/models/project/project.element.type';
import { UserCommandsResultElement }      from '@/models/project/user.commands.result.element';
import { FileBrowserMetadata }            from '@/screens/ide/file.browser.metadata';


/**
 *
 * @class UserCommandsElement
 *
 * @brief this project leaf is user command files (attached to scenarios)
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      November 2023
 *
 */
@ProjectElementFactoryDecorator('UserCommandsElement')
export class UserCommandsElement extends BlobElement {

  /** standard constructor */
  constructor(
    project:  Project,
    id:       string,
    type:     BlobType,
    key:      string,
    parent:   ProjectElement,
    publish?: boolean,
    label?:   string
  ) {

    super(project, id, type, key, parent, publish, label, ProjectElementType.UserCommands);

    this.actionKeys = [...this.actionKeys, 'viewerrors'];

  }
  /** refresh the icon display for this element */
  public updateIcon(alternate: string=''): void {

    /* the icon should be the icon used for that blob type */

    if (!this.hasErrors()) {

      /* no errors are possible, we are green! */

      this._icon    = 'blob-icons/blob-cmd-20.png';

      this.redraw();

      /* update the parent scenario's icon as well (if we can find it) */

      if (this.parent() && this.parent().blobType() == BlobType.Scenario) {

        this.parent().updateIcon('blob-scenario-user-commands-good-20.png');
      }

      return ;
    }

    /* there is at least one error somewhere */

    this._icon    = 'blob-icons/blob-cmd-error-20.png';

    this.redraw();

    /* update the parent scenario's icon as well (if we can find it) */

    if (this.parent() && this.parent().blobType() == BlobType.Scenario) {

      this.parent().updateIcon('blob-scenario-user-commands-bad-20.png');
    }

    return ;
  }

  /** helper to use to for static global constructors (and factory registration) */
  static requireType() {
    console.log("requiring UserCommandsElement");
  }

  /** initialize this element given the data in the simplified node object */
  public fromNode(node: any) {

    /* recover the basics */

    super.fromNode(node);

    /* icon is set per the job's known status */

    this.updateIcon();
  }

  /**
   * subclasses must add their menu item definition(s) to the given action set,
   * in jquery-contextmenu fashion.
   *
   * @param actionSet any
   *
   */
  public actions(actionSet: any): any {

    actionSet.viewerrors = {
      name: 'View Errors',
      icon: 'fas fa-bug',
      disabled: !this.hasErrors()
    }

    actionSet.viewinbrowser = {
      name: 'Folder Of',
      icon: 'fas fa-list',
      disabled: false
    };

    actionSet.tools = {

      name: 'Tools',
      items: {

        jsoneditblob:  {
          name: 'JSON Edit',
          icon: 'fas fa-edit',
          disabled: false
        },

        raweditblob: {
          name: 'Raw Edit',
          icon: 'fas fa-keyboard',
          disabled: false
        },

        download: {
          name: 'Download',
          icon: 'fas fa-file-download',
          disabled: false
        },

        downloadas: {
          name: 'Download As...',
          icon: 'fas fa-file-download',
          disabled: false
        }

      }
    };

    actionSet.renameblob = {
      name: 'Rename',
      icon: 'fas fa-tag',
      disabled:  this.isBusy()
    };

    actionSet.deleteblob = {
      name: 'Delete',
      icon: 'fas fa-trash-alt',
      disabled:  this.isBusy()
    };

    return actionSet;
  }

  /* check to see if we have any errors */
  public hasErrors(): boolean {

    let kids: UserCommandsResultElement[] = this.children() as UserCommandsResultElement[];

    /* no errors are possible, we are green! */

    if (!kids || kids.length == 0) {

      return false;
    }

    /* if there are children, but no errors, still green! */

    if (!kids.find(x => x.error_count > 0)) {

      return false;
    }

    /* there is at least one error somewhere */

    return true;
  }

  /** do the action for viewing errors */
  public viewErrors(): void {

    Logger.debug('[UserCommandsElement] viewErrors - fetching merge result...');

    if (!this.children() || this.children().length == 0) {

      /* no result yet, we should not have gotten here */

      return ;
    }

    this._project.services.blobber.contents(this.children()[0]._blobKey, 'latest', 'json')
      .pipe(first())
      .subscribe(
        (data: any): void => {

          Logger.debug(`[UserCommandsElement] viewErrors - result data: `, data);

          if (typeof data.errors == 'undefined') {

            /* badly formatted, no 'errors' member? */

            return ;
          }

          /* build up the raw text to display in a text panel */

          let text: string = '';

          text += `File: ${this._label}\n`;
          text += `Error Count: ${data.errors.length}\n\n`

          text += '\n--------------------------------------------------------------\n\n';

          data.errors.map((x: {line: any, markup: any}): {line: any, markup: any} => {

            return {markup: x.markup, line: x.line};}).forEach((item: {line: string; markup: any;}): void => {

              /* show the visual representation (markup) and a line number padded to 5 places */

              let s: string = "     " + item.line;

              text += `[${s.substr(s.length-5)}] ${item.markup}\n`;
          });

          text += '\n\n--------------------------------------------------------------\n\n';

          text += `File: ${this._label}\n`;
          text += `Error Count: ${data.errors.length}\n`

          text += '\n';
          text += `Validation Time (seconds): ${data.validation_time.toString()}\n`;
          text += `Merge Time (seconds): ${data.merge_time.toString()}\n`;
          text += `Total Time (seconds): ${data.total_time.toString()}\n`;
          text += '\n\n';

          /* open it in a text viewer */

          this._project.services.ide.launchWindow(new TextEditorMetadata(
            '',
            this._label,
            text,
            'rawtext'
          ));

        },
        (error): void => {

          Logger.error(`[UserCommandsElement] viewErrors - could not fetch result blob data`, errorAsText(error));

        });


  }

  /** subclasses must implement their own actions */
  public action(key: string, selection: ProjectElement[]) {

    switch (key) {

      case 'viewerrors': {

          this.viewErrors();
        }
        break;

      case 'jsoneditblob': {

          this._project.services.ide.launchWindow(new  JSONBrowserMetadata(
            '',
            '',
            this._blobKey,
            'json',
            basename(this._blobKey), '<i class="fas fa-edit"></i>'
          ));

        }
        break;

      case 'raweditblob': {

          this._project.services.ide.launchWindow(new TextEditorMetadata(
            '',
            '',
            this._blobKey
          ));

        }

        break;

      case 'download': {

          super.downloadBlob();

        }

        break;

      case 'downloadas': {

        super.downloadBlobAs();

        }

        break;

      case 'renameblob': {

          super.renameBlob();

        }
        break;

      case 'deleteblob': {

          Logger.debug('[UserCommandsElement] deleting', this._blobKey, this._blobType);

          super.deleteElements(selection);

        }
        break;

      case 'viewinbrowser': {

        this._project.services.ide.launchWindow(FileBrowserMetadata.build(this._blobKey, this._blobKey));
      }
      break;

    }

  }

}
